import { Injectable } from '@angular/core';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { map, Observable, of, take } from 'rxjs';

import { IProcessOrder } from 'src/app/shared/interfaces/IProcessOrder';
import { setSessionActive } from 'src/app/stores/global';
import { selectSettings } from 'src/app/stores/settings';
import { loadSpaces, loadSpacesFailure, loadSpacesSuccess } from 'src/app/stores/spaces';
import {
  isSizeSelectionAvailable,
  selectAvailableSpacesCount,
} from 'src/app/stores/spaces/spaces.selector';

import { DEVICE_TYPES } from '../../interfaces/ISettings';
import {
  ACTION_TYPES,
  FRONT_PATHS,
  MAX_USE_ADD_COMPARTMENT,
  MOBILE_TYPE,
  ORDER_ID_TYPE,
  TOAST_TYPES,
  VALID_FRONT_CODE_TYPES,
} from '../../utils/constants';
import { Utils } from '../../utils/utils';
import { ApiErrorHandlerService } from '../api-error-handler/api-error-handler.service';
import { HelmsdeepService } from '../helmsdeep/helmsdeep.service';
import { LoadFlowOriginService } from '../load-flow-origin/load-flow-origin.service';
import { FrontLoadingStore } from '../store/front-loading/front-loading.store';

@Injectable({ providedIn: 'root' })
export class FrontLoading {
  private lockerIsFullMessage = 'common.lockerIsFullMessage';
  type: string;
  flow: string;
  expireLoaded: boolean;
  invalidCodeMessage = 'common.invalidCodeMsg';
  selectSpacesToLoad = 'common.selectSpacesToLoad';
  enableOverflowArea: boolean;
  deviceType: string;
  isLockerFull = false;
  maxAddCompartment = Number(MAX_USE_ADD_COMPARTMENT);

  constructor(
    private helmsdeepService: HelmsdeepService,
    private apiErrorHandlerService: ApiErrorHandlerService,
    private utils: Utils,
    private readonly _frontLoadingStore: FrontLoadingStore,
    private store: Store,
    private actions$: Actions,
    private loadFlowOrigin: LoadFlowOriginService
  ) {
    this.store.select(selectSettings).subscribe((settings) => {
      this.enableOverflowArea = settings.device.enableOverflowArea;
      this.deviceType = settings.device.deviceType;
    });
  }

  init(processOrder: IProcessOrder): void {
    this.type = processOrder.type;
    this.flow = processOrder.flow;
    this.expireLoaded = processOrder.expireLoaded;
    this.utils.setLoadingValue(true);
    this.store.dispatch(loadSpaces());

    this.actions$
      .pipe(ofType(loadSpacesSuccess, loadSpacesFailure), take(1))
      .subscribe((action) => {
        if (action.type === loadSpacesFailure.type) {
          this.utils.setLoadingValue(false);
          this.apiErrorHandlerService.getErrorMessage(
            (action as ReturnType<typeof loadSpacesFailure>).error
          );
          this.abortFlowStartedByCode();
          return;
        }

        this.store
          .select(selectAvailableSpacesCount)
          .pipe(take(1))
          .subscribe((availableSpaces) => {
            const spacesAvailable =
              availableSpaces > this.maxAddCompartment ? this.maxAddCompartment : availableSpaces;
            this._frontLoadingStore.updateSpacesAvailable(spacesAvailable);

            if (availableSpaces > 0) {
              this.isLockerFull = false;
              this.processOrder(processOrder);
            } else {
              this.isLockerFull = true;
              this.apiErrorHandlerService.showMessage(this.lockerIsFullMessage, TOAST_TYPES.Error);
              this.overflowAreaManagement(processOrder);
              this.abortFlowStartedByCode();
            }
          });
      });
  }

  overflowAreaManagement(processOrder: IProcessOrder): void {
    if (!this.enableOverflowArea) {
      this.utils.setLoadingValue(false);
      return;
    }
    processOrder.enableOverflowArea = this.enableOverflowArea;
    this.appendScheduleProcessOrder(processOrder).subscribe({
      next: () => {
        this.utils.loadOverflowOrder(processOrder);
      },
      error: this.handleError.bind(this),
    });
  }

  private appendScheduleProcessOrder(processOrder: IProcessOrder): Observable<void> {
    if (processOrder.scheduleId) {
      return of(undefined);
    }

    let getScheduleId$: Observable<string> = this.getScheduleIdByLoadCode(`${processOrder.code}`);
    switch (this.type) {
      case ORDER_ID_TYPE:
        getScheduleId$ = this.createScheduleIdByOrderId(processOrder);
        break;
      case MOBILE_TYPE:
        getScheduleId$ = this.createScheduleIdByMobile(processOrder);
        break;
    }

    return getScheduleId$.pipe(
      map((scheduleId) => {
        processOrder.scheduleId = scheduleId;
      })
    );
  }

  processOrder(processOrder: IProcessOrder): void {
    if (processOrder.scheduleId) {
      this.initLoadWithOptionalSizeSelection(processOrder);
      return;
    }

    const appendScheduleId$ = this.appendScheduleProcessOrder(processOrder);
    appendScheduleId$.subscribe({
      next: () => {
        this.initLoadWithOptionalSizeSelection(processOrder);
      },
      error: this.handleError.bind(this),
    });
  }

  private handleError(error: any): void {
    this.utils.setLoadingValue(false);
    this.apiErrorHandlerService.getErrorMessage(error);
  }

  validateLoadCode(processOrder: IProcessOrder): void {
    if (processOrder.scheduleId) {
      this.initLoadWithOptionalSizeSelection(processOrder);
      return;
    }

    this.helmsdeepService.validateCode(`${processOrder.code}`, VALID_FRONT_CODE_TYPES).subscribe(
      (scheduleInfo) => {
        processOrder.scheduleId = scheduleInfo.scheduleId;
        this.initLoadWithOptionalSizeSelection(processOrder);
      },
      (error) => {
        this.handleError(error);
      }
    );
  }

  private getScheduleIdByLoadCode(code: string): Observable<string> {
    return this.helmsdeepService.validateCode(`${code}`, VALID_FRONT_CODE_TYPES).pipe(
      map((scheduleInfo) => {
        return scheduleInfo.scheduleId;
      })
    );
  }

  private createScheduleIdByOrderId(processOrder: IProcessOrder): Observable<string> {
    return this.utils.createOrderByOrderIdType(processOrder).pipe(
      map((response: any) => {
        return response.id;
      })
    );
  }

  private createScheduleIdByMobile(processOrder: IProcessOrder): Observable<string> {
    return this.utils.createOrderByMobileType(processOrder).pipe(
      map((response: any) => {
        localStorage.setItem('schedule', JSON.stringify(response));
        return response.id;
      })
    );
  }

  private initLoadWithOptionalSizeSelection(processOrder: IProcessOrder): void {
    processOrder.action = ACTION_TYPES.DELIVERY;
    processOrder.targetScreen = FRONT_PATHS.LOADING_ORDER;

    if (this.isArrayDevice()) {
      this.store
        .select(isSizeSelectionAvailable)
        .pipe(take(1))
        .subscribe((sizeSelectionAvailable) => {
          if (this.loadFlowOrigin.hasOrigin) {
            this.store.dispatch(setSessionActive({ value: true }));
          }
          if (sizeSelectionAvailable) {
            this.utils.setLoadingValue(false);
            this.utils.redirect(`${processOrder.frodoContext}/${FRONT_PATHS.SIZE_SELECTOR}`);
            return;
          } else {
            processOrder.targetScreen = FRONT_PATHS.GUIDED_LOADING_ORDER;
            this.utils.handleLoadProcess(processOrder);
            return;
          }
        });
      return;
    }

    this.utils.handleLoadProcess(processOrder);
  }

  /**
   * The array front-loading code entry screen is public: it marks where to
   * come back to before knowing whether the load can start. When it cannot —no room left, or the
   * spaces could not be loaded— the flow ends here without navigating anywhere, so have to be
   * released or the device is left with a stale return route.
   */
  private abortFlowStartedByCode(): void {
    if (!this.isArrayDevice() || !this.loadFlowOrigin.hasOrigin) {
      return;
    }

    this.loadFlowOrigin.clear();
  }

  private isArrayDevice(): boolean {
    return this.deviceType === DEVICE_TYPES.ARRAY_FL;
  }
}
